import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Wrench, X, CheckCircle2, XCircle, Search } from "lucide-react";

const SPECIALTIES = [
  "electrico", "plomeria", "refrigeracion", "cocina",
  "limpieza_industrial", "estructural", "tecnologia", "fumigacion", "otros",
];

const emptyForm = {
  name: "", contact_name: "", phone: "", email: "",
  rfc: "", specialty: "", address: "", notes: "",
};

export default function OpsMantenimientoProveedores() {
  const { user } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterSpecialty, setFilterSpecialty] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  async function loadSuppliers() {
    setLoading(true);
    try {
      const res = await api.get("/ops/maintenance/suppliers");
      setSuppliers(res.data || []);
    } catch (e) {
      toast.error("Error al cargar proveedores");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadSuppliers(); }, []);

  function openCreate() {
    setEditId(null);
    setForm(emptyForm);
    setShowModal(true);
  }

  function openEdit(sup) {
    setEditId(sup.id);
    setForm({
      name: sup.name || "",
      contact_name: sup.contact_name || "",
      phone: sup.phone || "",
      email: sup.email || "",
      rfc: sup.rfc || "",
      specialty: sup.specialty || "",
      address: sup.address || "",
      notes: sup.notes || "",
    });
    setShowModal(true);
  }

  async function handleSave() {
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/ops/maintenance/suppliers/${editId}`, form);
        toast.success("Proveedor actualizado");
      } else {
        await api.post("/ops/maintenance/suppliers", form);
        toast.success("Proveedor creado");
      }
      setShowModal(false);
      await loadSuppliers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(sup) {
    try {
      await api.put(`/ops/maintenance/suppliers/${sup.id}`, { active: !sup.active });
      toast.success(sup.active ? "Proveedor desactivado" : "Proveedor activado");
      await loadSuppliers();
    } catch (e) {
      toast.error("Error al cambiar estado");
    }
  }

  const filtered = suppliers.filter(s => {
    const matchSearch = !search || s.name.toLowerCase().includes(search.toLowerCase()) ||
      (s.contact_name || "").toLowerCase().includes(search.toLowerCase());
    const matchSpecialty = !filterSpecialty || s.specialty === filterSpecialty;
    return matchSearch && matchSpecialty;
  });

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-extrabold text-slate-900 flex items-center gap-2">
            <Wrench className="w-5 h-5 text-yellow-500" />
            Proveedores de mantenimiento
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Tecnicos, plomeros, electricistas y especialistas</p>
        </div>
        <button
          data-testid="mnt-sup-new-btn"
          onClick={openCreate}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#FFDF00] hover:bg-yellow-400 text-slate-900 font-semibold rounded-lg text-sm"
        >
          <Plus className="w-4 h-4" />
          Nuevo proveedor
        </button>
      </div>

      {/* Filtros */}
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="mnt-sup-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar proveedor..."
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm"
          />
        </div>
        <select
          data-testid="mnt-sup-specialty-filter"
          value={filterSpecialty}
          onChange={e => setFilterSpecialty(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">Todas las especialidades</option>
          {SPECIALTIES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Cargando...</div>
      ) : (
        <div className="space-y-3">
          {filtered.map(sup => (
            <div
              key={sup.id}
              data-testid={`mnt-sup-card-${sup.id}`}
              className={`bg-white border rounded-xl p-4 flex items-center justify-between gap-4 ${!sup.active ? "opacity-50" : "border-slate-200"}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-slate-800">{sup.name}</h3>
                  {sup.specialty && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">{sup.specialty}</span>
                  )}
                  {!sup.active && (
                    <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Inactivo</span>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                  {sup.contact_name && <span>{sup.contact_name}</span>}
                  {sup.phone && <span>{sup.phone}</span>}
                  {sup.email && <span>{sup.email}</span>}
                  {sup.rfc && <span>RFC: {sup.rfc}</span>}
                </div>
                {sup.notes && <p className="text-xs text-slate-400 mt-1 italic">{sup.notes}</p>}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  data-testid={`mnt-sup-edit-${sup.id}`}
                  onClick={() => openEdit(sup)}
                  className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  data-testid={`mnt-sup-toggle-${sup.id}`}
                  onClick={() => toggleActive(sup)}
                  className={`p-2 rounded-lg border text-sm font-medium transition-colors ${sup.active ? "border-red-200 text-red-500 hover:bg-red-50" : "border-green-200 text-green-600 hover:bg-green-50"}`}
                >
                  {sup.active ? <XCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              {suppliers.length === 0
                ? "Aún no hay proveedores de mantenimiento. Crea el primero."
                : "Sin resultados con los filtros actuales."}
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 space-y-4" data-testid="mnt-sup-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-slate-900">{editId ? "Editar proveedor" : "Nuevo proveedor"}</h3>
              <button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-sm font-medium text-slate-700 block mb-1">Nombre <span className="text-red-500">*</span></label>
                <input
                  data-testid="mnt-sup-name"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                  placeholder="Ej: Electrico Perez"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Contacto</label>
                <input
                  data-testid="mnt-sup-contact"
                  value={form.contact_name}
                  onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                  placeholder="Nombre del tecnico"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Telefono</label>
                <input
                  data-testid="mnt-sup-phone"
                  value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                  placeholder="+52..."
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Email</label>
                <input
                  data-testid="mnt-sup-email"
                  type="email"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                  placeholder="opcional"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">RFC</label>
                <input
                  data-testid="mnt-sup-rfc"
                  value={form.rfc}
                  onChange={e => setForm(f => ({ ...f, rfc: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                  placeholder="opcional"
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-slate-700 block mb-1">Especialidad</label>
                <select
                  data-testid="mnt-sup-specialty"
                  value={form.specialty}
                  onChange={e => setForm(f => ({ ...f, specialty: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="">Sin especialidad</option>
                  {SPECIALTIES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-slate-700 block mb-1">Direccion</label>
                <input
                  data-testid="mnt-sup-address"
                  value={form.address}
                  onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                  placeholder="opcional"
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-slate-700 block mb-1">Notas</label>
                <textarea
                  rows={2}
                  data-testid="mnt-sup-notes"
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none"
                  placeholder="opcional"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-600">Cancelar</button>
              <button
                data-testid="mnt-sup-save-btn"
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-2.5 bg-[#FFDF00] hover:bg-yellow-400 text-slate-900 font-semibold rounded-xl text-sm"
              >
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
